import type { FormikProps } from 'formik';
import { getIn } from 'formik';
import type { ReactChild } from 'react';
import { makePostRequest } from '../Utils/HttpRequest/makePostRequest';
import type { PersonalNumberValidationRequestDto } from '~/Features/Api/PersonalNumber/PersonalNumberValidationRequestDto.csharp';

export type ValidateOptions = {
    /* options to be passed to every validator. Ex: number of choice items */
    personalNumberValidationUrl?: string;
};

export type ValidatorFunction<T, O> = (arg: T, options?: O) => boolean | Promise<boolean>;

export type Validation<T, O> = [ValidatorFunction<T, O>, string | ReactChild];

export type FormFieldValidate<T> = (val: T) => Promise<string | ReactChild | undefined>;

/*
    every ASYNC form wizard validator (actually Formik validator) should return:
    - undefined if no errors
    - string with the error message if there's an error.
*/

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const combineValidators =
    <T>(validations: Validation<T, any>[]) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (val: T, ...combinedValidatorOptions: any[]): Promise<Awaited<ReturnType<FormFieldValidate<T>>>> => {
        // eslint-disable-next-line no-restricted-syntax
        for (const [validator, errorMessage = '--error--'] of validations) {
            const didPassValidator = await validator(val, ...combinedValidatorOptions);

            if (!didPassValidator) {
                return errorMessage;
            }
        }

        return undefined;
    };

export const isValidStudyVariantsList: ValidatorFunction<Array<unknown>, unknown> = val =>
    Array.isArray(val) && !!val.length;

export const isNotNull: ValidatorFunction<unknown | null, unknown> = val => val !== null;

export const isString: ValidatorFunction<string, unknown> = val => typeof val === 'string';

export const isNotEmptyString: ValidatorFunction<string, unknown> = val => !!val;

export const isNotEmptyObject: ValidatorFunction<unknown, unknown> = val =>
    !!val && typeof val === 'object' && val !== null && !!Object.keys(val).length;

export const isNotEmptyArray: ValidatorFunction<Array<unknown>, unknown> = val => !!val && !!val.length;

export const isNumericString: ValidatorFunction<string, unknown> = val => !val || val === String(parseInt(val, 10)); // empty string is valid

export const isAlphaNumericString: ValidatorFunction<string, unknown> = val => !val || HasOnlyDigitsAndLetters(val); // empty string is valid

const HasOnlyDigitsAndLetters = (val: string) => {
    const onlyDigitsAndLettersPattern = '^[A-Za-z0-9]+$';
    const matches = val.match(onlyDigitsAndLettersPattern);

    return !!matches && matches.length > 0;
};

type PersonalNumberResponseType = { isValid: boolean };

export const isPersonalNumberValidAsync = async (
    val: PersonalNumberValidationRequestDto,
    options: Pick<ValidateOptions, 'personalNumberValidationUrl'>
) => {
    if (!isString(val.personalNumber)) {
        return false;
    }

    if (!val.personalNumber) {
        return true; // no validation for emtpy string
    }

    if (!val.firstName || !val.lastName) {
        return true; // no validation for empty firstname or lastname
    }

    const defaultValidationResult = true;

    if (options.personalNumberValidationUrl) {
        const response = await makePostRequest<PersonalNumberResponseType>(options.personalNumberValidationUrl, val);
        switch (response.state) {
            case 'Success':
                if (!response.data.isValid) {
                    // Invalid only if "false" provided from validating endpoint [#285]
                    return false;
                }

                return defaultValidationResult;

            default:
                return defaultValidationResult;
        }
    }

    return defaultValidationResult;
};

const REGEXP_EMAIL = /^(?:(?:[A-Z0-9_%+-]\.?)+[A-Z0-9_%+-]|[A-Z0-9_%+-])@(?:[A-Z0-9-]+\.)+[A-Z]{2,4}$/i;
export const isEmail: ValidatorFunction<string, unknown> = val => !val || REGEXP_EMAIL.test(val); // no validation for empty string

export const isShallowEqual = <T>({ a, b }: { a: T; b: T }) => a === b;

export const isTrue: ValidatorFunction<boolean, unknown> = val => val === true;

const REGEXP_COUNTRY_CODE = /^([1-9]{1})([0-9]{1,4})$/;
export const isValidCountryCodeFormat: ValidatorFunction<string, unknown> = val =>
    !val || REGEXP_COUNTRY_CODE.test(val);

const REGEXP_PHONE_NUMBER = /^([1-9]{1})([0-9]{0,11}$)/;
export const isValidPhoneNumber: ValidatorFunction<string, unknown> = val => !val || REGEXP_PHONE_NUMBER.test(val);

const REGEXP_NORWEGIAN_PHONE_NUMBER = /^(4|9{1})([0-9]{7}$)/;
export const isValidNorwegianPhoneNumber: ValidatorFunction<string, unknown> = val =>
    !val || REGEXP_NORWEGIAN_PHONE_NUMBER.test(val);

export const isRequired: ValidatorFunction<boolean, unknown> = val => val;

/* helper */
export function readValidationErrorFromFormik<T>(
    formikErrors: FormikProps<T>['errors'],
    formikTouched: FormikProps<T>['touched'],
    fieldPath: string | string[],
    submitCount: number
) {
    const fieldError = getIn(formikErrors, fieldPath);
    const fieldTouched = getIn(formikTouched, fieldPath) || submitCount > 0;

    if (fieldTouched && fieldError) {
        return fieldError;
    }

    // no error
    return undefined;
}
